import { useEffect } from "react";
import { t } from "../../i18n";
import { seoFailureReason } from "./casinoSeoFailure";
import { SeoDraftLoadStatusType, SeoDraftSubscriptionType } from "./seoDraftStore";
import { useSeoDraftVersion } from "./useSeoDraftVersion";

/** The part of a casino SEO draft the shared editor shell reads. */
export interface SeoDraftStateType<TContext> extends SeoDraftSubscriptionType {
  ensureLoaded(id: number): void;
  getLoadedId(): number | undefined;
  getLoadStatus(): SeoDraftLoadStatusType | undefined;
  getLoadFailure(): unknown;
  isSaving(): boolean;
  getContext(): TContext | undefined;
}

export interface SeoDraftEditorShellProps<TContext> {
  draft: SeoDraftStateType<TContext>;
  /** Undefined until the target has been saved once. */
  targetId?: number;
  saveFirstMessage: string;
  saveFirstTestId: string;
  testIdPrefix: string;
  renderFields: (context: TContext) => JSX.Element;
}

/**
 * Loads a target's SEO into its draft and shows the state the editor is in: save the target first,
 * loading, failed to load, or the fields, locked while a save is out.
 */
export function SeoDraftEditorShell<TContext>({
  draft,
  targetId,
  saveFirstMessage,
  saveFirstTestId,
  testIdPrefix,
  renderFields,
}: SeoDraftEditorShellProps<TContext>): JSX.Element {
  useSeoDraftVersion(draft);

  useEffect(() => {
    if (targetId !== undefined) {
      draft.ensureLoaded(targetId);
    }
  }, [draft, targetId]);

  if (targetId === undefined) {
    return (
      <div
        className="alert alert-info"
        data-testid={saveFirstTestId}
      >
        {saveFirstMessage}
      </div>
    );
  }
  if (draft.getLoadStatus() === "failed") {
    return (
      <div
        className="alert alert-danger"
        data-testid={`${testIdPrefix}-load-failed`}
      >
        {t(
          "admin.seo.load-failed",
          { reason: seoFailureReason(draft.getLoadFailure()), interpolation: { escapeValue: false } },
          "SEO could not be loaded: {{reason}}",
        )}
      </div>
    );
  }
  const context = draft.getContext();
  if (draft.getLoadedId() !== targetId || context === undefined) {
    return <div data-testid={`${testIdPrefix}-loading`}>{t("admin.seo.loading", undefined, "Loading SEO…")}</div>;
  }
  // A disabled fieldset disables every control inside it, so nothing can be typed while the save is out.
  return (
    <fieldset
      disabled={draft.isSaving()}
      data-testid={`${testIdPrefix}-fields`}
    >
      {renderFields(context)}
    </fieldset>
  );
}
